import { getApiBase } from '../config/apiBase';
import { getToken, clearSession } from '../utils/session';

/** Dev → localhost:5000; Railway same-origin → /api; Vercel → Railway API URL */
const BASE = getApiBase();
const inflightGets = new Map();

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { headers, ...options });
  const text = await res.text();
  const trimmed = text.trim();
  const looksHtml = trimmed.startsWith('<!') || trimmed.startsWith('<html');

  if (looksHtml) {
    const hint = path.startsWith('/financials')
      ? ' The Financials API may be missing — stop and restart the Node server (from the server folder: npm start) so /api/financials routes load.'
      : path.startsWith('/inventory/aging')
        ? ' The Inventory Aging API may be missing — restart the Node server so /api/inventory/aging routes load.'
        : '';
    throw new Error(
      `Server returned a web page instead of JSON (often the React index.html). Check that the API is running on the same host as REACT_APP_API_URL / port 5000.${hint}`
    );
  }

  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(
      res.ok
        ? `Unexpected non-JSON response from server: ${text.slice(0, 120)}${text.length > 120 ? '…' : ''}`
        : `HTTP ${res.status}`
    );
  }

  if (res.ok && json.success) return json.data;

  // An expired session is not an error the caller can do anything with. The token
  // now lasts only as long as the idle limit, so this is what a tab left open
  // overnight meets first — and without it every screen would just show its own
  // failure message while the person stayed on a page they are no longer signed in
  // to. The idle dialog handles the common case; this catches the rest.
  if (res.status === 401 && getToken()) {
    clearSession();
    window.dispatchEvent(new CustomEvent('mrp:session-expired'));
  }

  const message = json.message || `HTTP ${res.status}`;
  throw new Error(message);
}

/** Multipart POST (e.g. CSV upload). Do not set Content-Type — browser sets boundary. */
async function postForm(path, formData) {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: formData });
  const text = await res.text();
  const trimmed = text.trim();
  const looksHtml = trimmed.startsWith('<!') || trimmed.startsWith('<html');
  if (looksHtml) {
    throw new Error(
      'Server returned a web page instead of JSON. Check that the API is running on port 5000 / REACT_APP_API_URL.'
    );
  }
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(
      res.ok
        ? `Unexpected non-JSON response: ${text.slice(0, 120)}${text.length > 120 ? '…' : ''}`
        : `HTTP ${res.status}`
    );
  }
  if (res.ok && json.success) return json.data;
  const message = json.message || `HTTP ${res.status}`;
  const err = new Error(message);
  err.data = json.data;
  err.status = res.status;
  throw err;
}

function del(path) {
  return request(path, { method: 'DELETE' });
}

function get(path) {
  if (inflightGets.has(path)) return inflightGets.get(path);
  const pending = request(path).finally(() => inflightGets.delete(path));
  inflightGets.set(path, pending);
  return pending;
}

const api = {
  get,
  post: (path, body = {}) => request(path, { method: 'POST', body: JSON.stringify(body) }),
  postForm: (path, formData) => postForm(path, formData),
  put:  (path, body) => request(path, { method: 'PUT',    body: JSON.stringify(body) }),
  patch: (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del,
  delete: del,
};

export { getApiBase } from '../config/apiBase';
export default api;
